import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
} from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { Trans, useTranslation } from "react-i18next"
import { useAdminUserRoles, useAssignAdminUserRoles } from "../hooks/useAdminUsers"
import { getErrorMessage } from "../utils"

interface Props {
  userId: number
}

export default function RolesTabContent({ userId }: Props) {
  const { t } = useTranslation()
  const { data: roles, isLoading } = useAdminUserRoles(userId)
  const assignMutation = useAssignAdminUserRoles()

  const [available, setAvailable] = useState<string[]>([])
  const [assigned, setAssigned] = useState<string[]>([])
  const [selectedAvailable, setSelectedAvailable] = useState<Set<string>>(new Set())
  const [selectedAssigned, setSelectedAssigned] = useState<Set<string>>(new Set())
  const [searchAvailable, setSearchAvailable] = useState("")
  const [searchAssigned, setSearchAssigned] = useState("")
  const [hasChanges, setHasChanges] = useState(false)

  const originalAvailable = useMemo(() => roles?.available ?? [], [roles])
  const originalAssigned = useMemo(() => roles?.assigned ?? [], [roles])

  useEffect(() => {
    if (roles) {
      setAvailable([...roles.available])
      setAssigned([...roles.assigned])
      setSelectedAvailable(new Set())
      setSelectedAssigned(new Set())
      setHasChanges(false)
    }
  }, [roles])

  const filteredAvailable = useMemo(
    () => available.filter((r) => r.toLowerCase().includes(searchAvailable.toLowerCase())),
    [available, searchAvailable],
  )

  const filteredAssigned = useMemo(
    () => assigned.filter((r) => r.toLowerCase().includes(searchAssigned.toLowerCase())),
    [assigned, searchAssigned],
  )

  const toggleAvailable = (name: string) => {
    setSelectedAvailable((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const toggleAssigned = (name: string) => {
    setSelectedAssigned((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const handleAssign = () => {
    const toAssign = available.filter((r) => selectedAvailable.has(r))
    setAssigned((prev) => [...prev, ...toAssign].sort())
    setAvailable((prev) => prev.filter((r) => !selectedAvailable.has(r)))
    setSelectedAvailable(new Set())
    setHasChanges(true)
  }

  const handleRevoke = () => {
    const toRevoke = assigned.filter((r) => selectedAssigned.has(r))
    setAvailable((prev) => [...prev, ...toRevoke].sort())
    setAssigned((prev) => prev.filter((r) => !selectedAssigned.has(r)))
    setSelectedAssigned(new Set())
    setHasChanges(true)
  }

  const handleSave = () => {
    const add = assigned.filter((r) => !originalAssigned.includes(r))
    const remove = originalAssigned.filter((r) => !assigned.includes(r))

    assignMutation.mutate(
      { id: userId, assign: add, revoke: remove },
      {
        onSuccess: () => {
          notifications.show({
            color: "green",
            title: t("common.success"),
            message: t("adminUsers.rolesUpdateSuccess"),
          })
          setHasChanges(false)
        },
        onError: (err) => {
          notifications.show({
            color: "red",
            title: t("common.error"),
            message: getErrorMessage(err),
          })
        },
      },
    )
  }

  if (isLoading) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="xl">
        <i className="fa fa-spinner fa-spin" /> {t("common.loading")}
      </Text>
    )
  }

  return (
    <Stack>
      {assignMutation.isError && (
        <Alert color="red" withCloseButton onClose={() => assignMutation.reset()}>
          {assignMutation.error?.message}
        </Alert>
      )}

      <Group gap="md" align="flex-start" grow>
        <Paper withBorder p="sm" style={{ flex: 1 }}>
          <Text size="sm" fw={600} mb="sm">
            {t("adminUsers.availableRoles")}
          </Text>
          <TextInput
            placeholder={t("adminUsers.searchRoles")}
            value={searchAvailable}
            onChange={(e) => setSearchAvailable(e.currentTarget.value)}
            size="xs"
            mb="xs"
          />
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {filteredAvailable.length === 0 ? (
              <Text size="xs" c="dimmed" ta="center" py="md">
                {t("adminUsers.noAvailableRoles")}
              </Text>
            ) : (
              <Stack gap={2}>
                {filteredAvailable.map((role) => (
                  <Checkbox
                    key={role}
                    label={role}
                    size="xs"
                    checked={selectedAvailable.has(role)}
                    onChange={() => toggleAvailable(role)}
                  />
                ))}
              </Stack>
            )}
          </div>
        </Paper>

        <Stack justify="center" gap="xs" style={{ flex: "0 0 auto", paddingTop: 60 }}>
          <Button
            size="xs"
            variant="default"
            disabled={selectedAvailable.size === 0}
            onClick={handleAssign}
          >
            Assign &rarr;
          </Button>
          <Button
            size="xs"
            variant="default"
            disabled={selectedAssigned.size === 0}
            onClick={handleRevoke}
          >
            &larr; Revoke
          </Button>
        </Stack>

        <Paper withBorder p="sm" style={{ flex: 1 }}>
          <Text size="sm" fw={600} mb="sm">
            {t("adminUsers.assignedRoles")}
          </Text>
          <TextInput
            placeholder={t("adminUsers.searchRoles")}
            value={searchAssigned}
            onChange={(e) => setSearchAssigned(e.currentTarget.value)}
            size="xs"
            mb="xs"
          />
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {filteredAssigned.length === 0 ? (
              <Text size="xs" c="dimmed" ta="center" py="md">
                {t("adminUsers.noAssignedRoles")}
              </Text>
            ) : (
              <Stack gap={2}>
                {filteredAssigned.map((role) => (
                  <Checkbox
                    key={role}
                    label={role}
                    size="xs"
                    checked={selectedAssigned.has(role)}
                    onChange={() => toggleAssigned(role)}
                  />
                ))}
              </Stack>
            )}
          </div>
        </Paper>
      </Group>

      {hasChanges && (
        <Group justify="center">
          <Button
            onClick={handleSave}
            loading={assignMutation.isPending}
            leftSection={<i className="fa fa-save" />}
          >
            <Trans i18nKey="common.saveChanges" />
          </Button>
        </Group>
      )}
    </Stack>
  )
}
